import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const AdminPortal = ({ onBackToHome, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalClients: 0,
    totalTrainers: 0,
    totalSessions: 0,
    totalRevenue: 0,
    activeSessions: 0
  });
  const [clients, setClients] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [referenceVideos, setReferenceVideos] = useState([]);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [processingApplication, setProcessingApplication] = useState(null);
  const [processingTrainer, setProcessingTrainer] = useState(null);
  const [billingData, setBillingData] = useState({ sessionIncome: 0, sessionCount: 0 });
  const [dashboardStartDate, setDashboardStartDate] = useState('');
  const [dashboardEndDate, setDashboardEndDate] = useState('');
  const [trainerSearchQuery, setTrainerSearchQuery] = useState('');
  const [clientTotalCount, setClientTotalCount] = useState(0);
  const [trainerTotalCount, setTrainerTotalCount] = useState(0);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    category: '',
    url: '',
    isActive: true
  });
  const [deleteUsername, setDeleteUsername] = useState('');
  const [deleteStatus, setDeleteStatus] = useState({ type: '', message: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      // Load all admin data
      await Promise.all([
        loadStats(),
        loadClients(),
        loadTrainers(),
        loadSessions(),
        loadReferenceVideos(),
        loadPendingApplications(),
        loadBilling()
      ]);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.getAdminStats(dashboardStartDate || null, dashboardEndDate || null);
      if (response.Code === 200 && response.data) {
        setStats({
          totalClients: response.data.totalClients || 0,
          totalTrainers: response.data.totalTrainers || 0,
          totalSessions: response.data.totalSessions || 0,
          totalRevenue: response.data.totalRevenue || 0,
          activeSessions: response.data.activeSessions || 0
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllClients();
      if (response.Code === 200 && response.data) {
        setClients(response.data);
        setClientTotalCount(response.totalCount || response.data.length);
      } else {
        console.error('Failed to load clients:', response.Message);
        setError(response.Message || 'Failed to load clients');
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadTrainers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllTrainersForAdmin();
      if (response.Code === 200 && response.data) {
        setTrainers(response.data);
        setTrainerTotalCount(response.totalCount || response.data.length);
      } else {
        console.error('Failed to load trainers:', response.Message);
        setError(response.Message || 'Failed to load trainers');
      }
    } catch (err) {
      console.error('Error loading trainers:', err);
      setError('Failed to load trainers: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await apiService.getAllSessions(trainerSearchQuery || null);
      if (response.Code === 200 && response.data) {
        setSessions(response.data);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const loadBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getBilling();
      if (response.Code === 200 && response.data) {
        setBillingData({
          sessionIncome: response.data.sessionIncome || 0,
          sessionCount: response.data.sessionCount || 0
        });
      } else {
        console.error('Failed to load billing:', response.Message);
        setError(response.Message || 'Failed to load billing data');
      }
    } catch (err) {
      console.error('Error loading billing:', err);
      setError('Failed to load billing data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceVideos = async () => {
    // Mock data for now
    setReferenceVideos([
      { id: 1, title: 'Basic Sit Command', category: 'Obedience', isActive: true, uploadDate: '2024-01-15' },
      { id: 2, title: 'Leash Training Basics', category: 'Walking', isActive: true, uploadDate: '2024-01-20' },
      { id: 3, title: 'Puppy Socialization', category: 'Puppy', isActive: false, uploadDate: '2024-02-01' }
    ]);
  };

  const loadPendingApplications = async () => {
    try {
      const response = await apiService.getPendingTrainerApplications();
      if (response.Code === 200) {
        setPendingApplications(response.data || []);
      }
    } catch (err) {
      console.error('Error loading pending applications:', err);
    }
  };

  const handleAcceptApplication = async (trainerId) => {
    if (!window.confirm('Are you sure you want to accept this trainer application?')) {
      return;
    }
    
    try {
      setProcessingApplication(trainerId);
      const response = await apiService.acceptTrainerApplication(trainerId);
      if (response.Code === 200) {
        await loadPendingApplications();
        await loadTrainers();
        alert('Trainer application accepted successfully');
      } else {
        alert('Failed to accept application: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error accepting application:', err);
      alert('Failed to accept application: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleDenyApplication = async (trainerId) => {
    const reason = window.prompt('Please provide a reason for denying this application (optional):');
    if (reason === null) {
      return; // User cancelled
    }
    
    if (!window.confirm('Are you sure you want to deny this trainer application? This will remove their account.')) {
      return;
    }
    
    try {
      setProcessingApplication(trainerId);
      const response = await apiService.denyTrainerApplication(trainerId, reason || '');
      if (response.Code === 200) {
        await loadPendingApplications();
        await loadTrainers();
        alert('Trainer application denied and moved to blocked trainers');
      } else {
        alert('Failed to deny application: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error denying application:', err);
      alert('Failed to deny application: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleAddVideo = async () => {
    try {
      setLoading(true);
      // Add video logic here
      const newVideoData = {
        ...newVideo,
        id: referenceVideos.length + 1,
        uploadDate: new Date().toISOString().split('T')[0]
      };
      setReferenceVideos([...referenceVideos, newVideoData]);
      setNewVideo({ title: '', description: '', category: '', url: '', isActive: true });
      setError(null);
    } catch (err) {
      setError('Failed to add video');
    } finally {
      setLoading(false);
    }
  };

  const toggleVideoStatus = (videoId) => {
    setReferenceVideos(videos => 
      videos.map(video => 
        video.id === videoId 
          ? { ...video, isActive: !video.isActive }
          : video
      )
    );
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deleteUsername.trim()) {
      setDeleteStatus({ type: 'error', message: 'Please enter a username to delete.' });
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteStatus({ type: '', message: '' });

      const response = await apiService.deleteUserByUsername(deleteUsername.trim());

      if (response.Code === 200) {
        setDeleteStatus({
          type: 'success',
          message: `User '${deleteUsername.trim()}' was deleted successfully.`
        });
        setDeleteUsername('');
      } else {
        setDeleteStatus({
          type: 'error',
          message: response.Message || 'Failed to delete user.'
        });
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      setDeleteStatus({
        type: 'error',
        message: err.message || 'Failed to delete user.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      minHeight: '100vh',
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: '#2c3e50',
      color: 'white',
      padding: '20px 0',
      boxShadow: '0 2px 10px rgba(44, 62, 80, 0.2)'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      cursor: 'pointer'
    },
    logoIcon: {
      width: '40px',
      height: '40px',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.2rem'
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: 'bold'
    },
    headerActions: {
      display: 'flex',
      gap: '15px',
      alignItems: 'center'
    },
    button: {
      background: 'transparent',
      border: '2px solid #ecf0f1',
      color: '#ecf0f1',
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'all 0.3s ease'
    },
    logoutButton: {
      background: 'linear-gradient(45deg, #e74c3c, #c0392b)',
      border: '2px solid #e74c3c',
      color: 'white'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%',
      padding: '20px'
    },
    sidebar: {
      width: '250px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '15px',
      padding: '20px',
      marginRight: '20px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      height: 'fit-content'
    },
    tabButton: {
      width: '100%',
      padding: '15px 20px',
      marginBottom: '10px',
      background: 'transparent',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '10px',
      color: '#ecf0f1',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    tabButtonActive: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      border: '2px solid #e67e22',
      color: 'white',
      transform: 'translateX(5px)'
    },
    tabIcon: {
      fontSize: '1.2rem'
    },
    contentArea: {
      flex: 1,
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '15px',
      padding: '30px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxHeight: '80vh',
      overflowY: 'auto'
    },
    tabTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#f39c12'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '10px',
      padding: '20px',
      textAlign: 'center',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#f39c12',
      marginBottom: '5px'
    },
    statLabel: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.8)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '10px',
      overflow: 'hidden'
    },
    tableHeader: {
      background: 'rgba(243, 156, 18, 0.2)',
      color: '#f39c12',
      fontWeight: '600',
      padding: '15px',
      textAlign: 'left'
    },
    tableCell: {
      padding: '15px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      color: 'white'
    },
    statusBadge: {
      padding: '5px 10px',
      borderRadius: '15px',
      fontSize: '0.8rem',
      fontWeight: '600'
    },
    statusActive: {
      background: 'rgba(46, 204, 113, 0.2)',
      color: '#2ecc71'
    },
    statusInactive: {
      background: 'rgba(231, 76, 60, 0.2)',
      color: '#e74c3c'
    },
    statusPending: {
      background: 'rgba(241, 196, 15, 0.2)',
      color: '#f1c40f'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      maxWidth: '500px'
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#f39c12'
    },
    input: {
      padding: '15px',
      borderRadius: '10px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      transition: 'all 0.3s ease'
    },
    textarea: {
      padding: '15px',
      borderRadius: '10px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    select: {
      padding: '15px',
      borderRadius: '10px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      cursor: 'pointer'
    },
    submitButton: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '10px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      alignSelf: 'flex-start'
    },
    toggleButton: {
      background: 'transparent',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      color: 'white',
      padding: '5px 10px',
      borderRadius: '5px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    error: {
      background: 'rgba(231, 76, 60, 0.2)',
      border: '1px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    success: {
      background: 'rgba(46, 204, 113, 0.2)',
      border: '1px solid #2ecc71',
      color: '#2ecc71',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    loading: {
      textAlign: 'center',
      color: '#f39c12',
      fontSize: '1.1rem',
      marginTop: '20px'
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'clients', label: 'Clients', icon: '👥' },
    { id: 'trainers', label: 'Trainers', icon: '🎓' },
    { id: 'pending', label: 'Pending Applications', icon: '⏳' },
    { id: 'sessions', label: 'Sessions', icon: '📅' },
    { id: 'videos', label: 'Reference Videos', icon: '🎥' },
    { id: 'billing', label: 'Billing', icon: '💰' },
    { id: 'accounts', label: 'Account Tools', icon: '🛠️' }
  ];

  const renderDashboard = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={styles.tabTitle}>Admin Dashboard</h2>
        <button
          onClick={loadStats}
          style={{
            ...styles.button,
            background: '#3b82f6',
            padding: '8px 16px'
          }}
        >
          🔄 Refresh
        </button>
      </div>
      
      <div style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ color: '#f39c12', fontSize: '0.9rem', fontWeight: '600' }}>Start Date</label>
          <input
            type="date"
            value={dashboardStartDate}
            onChange={(e) => setDashboardStartDate(e.target.value)}
            style={{
              ...styles.input,
              padding: '10px',
              width: '200px'
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ color: '#f39c12', fontSize: '0.9rem', fontWeight: '600' }}>End Date</label>
          <input
            type="date"
            value={dashboardEndDate}
            onChange={(e) => setDashboardEndDate(e.target.value)}
            style={{
              ...styles.input,
              padding: '10px',
              width: '200px'
            }}
          />
        </div>
        <button
          onClick={loadStats}
          style={{
            ...styles.button,
            background: '#22c55e',
            padding: '10px 20px',
            marginTop: '20px'
          }}
        >
          Apply Date Range
        </button>
      </div>
      
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalClients}</div>
          <div style={styles.statLabel}>Total Clients</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalTrainers}</div>
          <div style={styles.statLabel}>Total Trainers</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalSessions}</div>
          <div style={styles.statLabel}>Total Sessions</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>${stats.totalRevenue.toLocaleString()}</div>
          <div style={styles.statLabel}>Total Revenue</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.activeSessions}</div>
          <div style={styles.statLabel}>Active Sessions</div>
        </div>
      </div>
    </div>
  );

  const renderClients = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={styles.tabTitle}>Client Management</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '5px' }}>
            Total Clients (Last Month): {clientTotalCount}
          </p>
        </div>
        <button
          onClick={loadClients}
          style={{
            ...styles.button,
            background: '#3b82f6',
            padding: '8px 16px'
          }}
        >
          🔄 Refresh
        </button>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Name</th>
            <th style={styles.tableHeader}>Email</th>
            <th style={styles.tableHeader}>Join Date</th>
            <th style={styles.tableHeader}>Sessions</th>
            <th style={styles.tableHeader}>Status</th>
          </tr>
        </thead>
        <tbody>
          {clients.map(client => (
            <tr key={client.id}>
              <td style={styles.tableCell}>{client.name}</td>
              <td style={styles.tableCell}>{client.email}</td>
              <td style={styles.tableCell}>{client.joinDate}</td>
              <td style={styles.tableCell}>{client.sessions}</td>
              <td style={styles.tableCell}>
                <span style={{
                  ...styles.statusBadge,
                  ...(client.status === 'Active' ? styles.statusActive : styles.statusInactive)
                }}>
                  {client.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleAcceptTrainer = async (trainerId) => {
    if (!window.confirm('Are you sure you want to approve this trainer?')) {
      return;
    }
    
    try {
      setProcessingTrainer(trainerId);
      const response = await apiService.acceptTrainerApplication(trainerId);
      if (response.Code === 200) {
        await loadTrainers();
        await loadStats(); // Refresh stats to update trainer count
        alert('Trainer approved successfully');
      } else {
        alert('Failed to approve trainer: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error approving trainer:', err);
      alert('Failed to approve trainer: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingTrainer(null);
    }
  };

  const handleDenyTrainer = async (trainerId) => {
    const reason = window.prompt('Please provide a reason for denying this trainer (optional):');
    if (reason === null) {
      return; // User cancelled
    }
    
    if (!window.confirm('Are you sure you want to deny this trainer? This will remove their account.')) {
      return;
    }
    
    try {
      setProcessingTrainer(trainerId);
      const response = await apiService.denyTrainerApplication(trainerId, reason || '');
      if (response.Code === 200) {
        await loadTrainers();
        await loadStats(); // Refresh stats to update trainer count
        alert('Trainer denied and removed from system');
      } else {
        alert('Failed to deny trainer: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error denying trainer:', err);
      alert('Failed to deny trainer: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessingTrainer(null);
    }
  };

  const renderTrainers = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={styles.tabTitle}>Trainer Management</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '5px' }}>
            Total Trainers (Last Month): {trainerTotalCount}
          </p>
        </div>
        <button
          onClick={loadTrainers}
          style={{
            ...styles.button,
            background: '#3b82f6',
            padding: '8px 16px'
          }}
        >
          🔄 Refresh
        </button>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Name</th>
            <th style={styles.tableHeader}>Email</th>
            <th style={styles.tableHeader}>Join Date</th>
            <th style={styles.tableHeader}>Rating</th>
            <th style={styles.tableHeader}>Sessions</th>
            <th style={styles.tableHeader}>Status</th>
            <th style={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {trainers.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ ...styles.tableCell, textAlign: 'center', padding: '40px' }}>
                No trainers found
              </td>
            </tr>
          ) : (
            trainers.map(trainer => (
              <tr key={trainer.id}>
                <td style={styles.tableCell}>{trainer.name}</td>
                <td style={styles.tableCell}>{trainer.email}</td>
                <td style={styles.tableCell}>{trainer.joinDate}</td>
                <td style={styles.tableCell}>{trainer.rating} ⭐</td>
                <td style={styles.tableCell}>{trainer.sessions}</td>
                <td style={styles.tableCell}>
                  <span style={{
                    ...styles.statusBadge,
                    ...(trainer.status === 'Active' ? styles.statusActive : 
                         trainer.status === 'Pending' ? styles.statusPending : styles.statusInactive)
                  }}>
                    {trainer.status}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  {trainer.status === 'Pending' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAcceptTrainer(trainer.id)}
                        disabled={processingTrainer === trainer.id}
                        style={{
                          ...styles.button,
                          background: '#22c55e',
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          border: 'none'
                        }}
                      >
                        {processingTrainer === trainer.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleDenyTrainer(trainer.id)}
                        disabled={processingTrainer === trainer.id}
                        style={{
                          ...styles.button,
                          background: '#ef4444',
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          border: 'none'
                        }}
                      >
                        {processingTrainer === trainer.id ? 'Processing...' : 'Deny'}
                      </button>
                    </div>
                  )}
                  {trainer.status === 'Active' && (
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>
                      Approved
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderSessions = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2 style={styles.tabTitle}>Session Management</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by trainer name or email..."
            value={trainerSearchQuery}
            onChange={(e) => setTrainerSearchQuery(e.target.value)}
            style={{
              ...styles.input,
              padding: '10px',
              width: '300px'
            }}
          />
          <button
            onClick={loadSessions}
            style={{
              ...styles.button,
              background: '#3b82f6',
              padding: '10px 16px'
            }}
          >
            🔍 Search
          </button>
          <button
            onClick={() => {
              setTrainerSearchQuery('');
              loadSessions();
            }}
            style={{
              ...styles.button,
              background: '#64748b',
              padding: '8px 16px'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>
      {trainerSearchQuery && (
        <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '15px' }}>
          Showing all-time sessions for trainer: "{trainerSearchQuery}"
        </p>
      )}
      {!trainerSearchQuery && (
        <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '15px' }}>
          Showing sessions from the last month
        </p>
      )}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Client</th>
            <th style={styles.tableHeader}>Trainer</th>
            <th style={styles.tableHeader}>Date</th>
            <th style={styles.tableHeader}>Duration</th>
            <th style={styles.tableHeader}>Status</th>
            <th style={styles.tableHeader}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {sessions.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ ...styles.tableCell, textAlign: 'center', padding: '40px' }}>
                No sessions found
              </td>
            </tr>
          ) : (
            sessions.map(session => (
              <tr key={session.id}>
                <td style={styles.tableCell}>{session.client}</td>
                <td style={styles.tableCell}>{session.trainer}</td>
                <td style={styles.tableCell}>{session.date}</td>
                <td style={styles.tableCell}>{session.duration} min</td>
                <td style={styles.tableCell}>
                  <span style={{
                    ...styles.statusBadge,
                    ...(session.status === 'Completed' ? styles.statusActive : 
                         session.status === 'Scheduled' ? styles.statusPending : styles.statusInactive)
                  }}>
                    {session.status}
                  </span>
                </td>
                <td style={styles.tableCell}>${session.revenue.toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderVideos = () => (
    <div>
      <h2 style={styles.tabTitle}>Reference Video Management</h2>
      
      <div style={styles.form}>
        <h3 style={{ color: '#f39c12', marginBottom: '20px' }}>Add New Video</h3>
        <div style={styles.field}>
          <label style={styles.label}>Title</label>
          <input
            type="text"
            value={newVideo.title}
            onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
            style={styles.input}
            placeholder="Video title"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Description</label>
          <textarea
            value={newVideo.description}
            onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
            style={styles.textarea}
            placeholder="Video description"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Category</label>
          <select
            value={newVideo.category}
            onChange={(e) => setNewVideo({...newVideo, category: e.target.value})}
            style={styles.select}
          >
            <option value="">Select category</option>
            <option value="Obedience">Obedience</option>
            <option value="Walking">Walking</option>
            <option value="Puppy">Puppy</option>
            <option value="Behavior">Behavior</option>
            <option value="Training">Training</option>
          </select>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Video URL</label>
          <input
            type="url"
            value={newVideo.url}
            onChange={(e) => setNewVideo({...newVideo, url: e.target.value})}
            style={styles.input}
            placeholder="https://example.com/video"
          />
        </div>
        <button style={styles.submitButton} onClick={handleAddVideo}>
          Add Video
        </button>
      </div>

      <div style={{ marginTop: '40px' }}>
        <h3 style={{ color: '#f39c12', marginBottom: '20px' }}>Existing Videos</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Title</th>
              <th style={styles.tableHeader}>Category</th>
              <th style={styles.tableHeader}>Upload Date</th>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {referenceVideos.map(video => (
              <tr key={video.id}>
                <td style={styles.tableCell}>{video.title}</td>
                <td style={styles.tableCell}>{video.category}</td>
                <td style={styles.tableCell}>{video.uploadDate}</td>
                <td style={styles.tableCell}>
                  <span style={{
                    ...styles.statusBadge,
                    ...(video.isActive ? styles.statusActive : styles.statusInactive)
                  }}>
                    {video.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <button
                    style={styles.toggleButton}
                    onClick={() => toggleVideoStatus(video.id)}
                  >
                    {video.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderBilling = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={styles.tabTitle}>Billing & Revenue</h2>
        <button
          onClick={loadBilling}
          style={{
            ...styles.button,
            background: '#3b82f6',
            padding: '8px 16px'
          }}
        >
          🔄 Refresh
        </button>
      </div>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>${billingData.sessionIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div style={styles.statLabel}>Session Income</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{billingData.sessionCount.toLocaleString()}</div>
          <div style={styles.statLabel}>Session Count</div>
        </div>
      </div>
    </div>
  );

  const renderPendingApplications = () => (
    <div>
      <h2 style={styles.tabTitle}>Pending Trainer Applications</h2>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={styles.table}>
          <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 10 }}>
            <tr>
              <th style={styles.tableHeader}>Name</th>
              <th style={styles.tableHeader}>Email</th>
              <th style={styles.tableHeader}>Phone</th>
              <th style={styles.tableHeader}>Business Name</th>
              <th style={styles.tableHeader}>Experience (Years)</th>
              <th style={styles.tableHeader}>Rate ($/10min)</th>
              <th style={styles.tableHeader}>Payment Status</th>
              <th style={styles.tableHeader}>Submitted</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingApplications.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ ...styles.tableCell, textAlign: 'center', padding: '40px' }}>
                  No pending applications
                </td>
              </tr>
            ) : (
              pendingApplications.map(app => (
                <tr key={app.id}>
                  <td style={styles.tableCell}>{app.first_name} {app.last_name}</td>
                  <td style={styles.tableCell}>{app.email}</td>
                  <td style={styles.tableCell}>{app.phone_number || 'N/A'}</td>
                  <td style={styles.tableCell}>{app.business_name || 'N/A'}</td>
                  <td style={styles.tableCell}>{app.experience_years || 'N/A'}</td>
                  <td style={styles.tableCell}>${app.first_10_min_rate || 'N/A'}</td>
                  <td style={styles.tableCell}>
                    <span style={{
                      ...styles.statusBadge,
                      ...(app.payment_completed ? styles.statusActive : styles.statusPending)
                    }}>
                      {app.payment_completed ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    {app.application_submitted_at 
                      ? new Date(app.application_submitted_at).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAcceptApplication(app.id)}
                        disabled={processingApplication === app.id}
                        style={{
                          ...styles.button,
                          background: '#22c55e',
                          padding: '6px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        {processingApplication === app.id ? 'Processing...' : 'Accept'}
                      </button>
                      <button
                        onClick={() => handleDenyApplication(app.id)}
                        disabled={processingApplication === app.id}
                        style={{
                          ...styles.button,
                          background: '#ef4444',
                          padding: '6px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        {processingApplication === app.id ? 'Processing...' : 'Deny'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAccounts = () => (
    <div>
      <h2 style={styles.tabTitle}>Account Management Tools</h2>
      <p style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '20px', lineHeight: 1.6 }}>
        Remove user accounts directly by username. This action deletes the user and their associated profile data.
        Use with caution—deleted accounts cannot be recovered.
      </p>

      <form style={styles.form} onSubmit={handleDeleteAccount}>
        <div style={styles.field}>
          <label style={styles.label}>Username</label>
          <input
            type="text"
            value={deleteUsername}
            onChange={(e) => setDeleteUsername(e.target.value)}
            style={styles.input}
            placeholder="Enter username to delete"
          />
        </div>

        <button
          type="submit"
          style={{
            ...styles.submitButton,
            background: 'linear-gradient(45deg, #e74c3c, #c0392b)'
          }}
          disabled={deleteLoading}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Account'}
        </button>
      </form>

      {deleteStatus.message && (
        <div style={deleteStatus.type === 'success' ? styles.success : styles.error}>
          {deleteStatus.message}
        </div>
      )}
    </div>
  );

  const renderCurrentTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'clients':
        return renderClients();
      case 'trainers':
        return renderTrainers();
      case 'sessions':
        return renderSessions();
      case 'videos':
        return renderVideos();
      case 'billing':
        return renderBilling();
      case 'pending':
        return renderPendingApplications();
      case 'accounts':
        return renderAccounts();
      default:
        return renderDashboard();
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo} onClick={onBackToHome}>
            <img 
              src="/images/logo.png" 
              alt="Pheefee Logo" 
              style={{...styles.logoIcon, width: '40px', height: '40px', objectFit: 'contain'}}
            />
            <span style={styles.logoText}>Pheefee - Admin</span>
          </div>
          <div style={styles.headerActions}>
            <button
              onClick={onBackToHome}
              style={styles.button}
            >
              ← Back to Home
            </button>
            <button
              onClick={onLogout}
              style={{...styles.button, ...styles.logoutButton}}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Left Sidebar */}
        <div style={styles.sidebar}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab.id ? styles.tabButtonActive : {})
              }}
            >
              <span style={styles.tabIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {error && <div style={styles.error}>{error}</div>}
          {loading && <div style={styles.loading}>Loading admin data...</div>}
          {renderCurrentTab()}
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;

